
import React, {useState} from 'react';
import moment from 'moment-timezone';
import { changeClassnameText } from "src/MarketDataComponents/Helpers";
import { EQUITY } from 'src/Api';

export default function Performance({performance, quote, instrumentInfo}) {
    
    const [tab, setTab] = useState('performance1D');
    const [performancePeriods] = useState({
        performance1D: "1D",
        performance1M: "1M",
        performance3M: "3M",
        performance1Y: "1Y",
        performance3Y: "3Y ann",
        performance5Y: "5Y ann",
        performance10Y: "10Y ann",
    });
    const [performanceCats] = useState({
        performance1D: "benchmarkPerformance1D",
        performance1M: "benchmarkPerformance1M",
        performance3M: "benchmarkPerformance3M",
        performance1Y: "benchmarkPerformance1Y",
        performance3Y: "benchmarkPerformance3Y",
        performance5Y: "benchmarkPerformance5Y",
        performance10Y: "benchmarkPerformance10Y",
    });
    const [marketReference] = useState({
        ['FTSE 100']: "/FTSE:UKX",
        ['FTSE 250']: "/FTSE:MCX",
        ['FTSE AIM All Share']: "/FTSE:AXX"
    });

    const moreLink = `sub/${quote.marketCode}/charts-performance`;

    const generatePerformanceData = (performance) => {
        const performanceData = {};

        try { 
            Object.keys(performance).map(key => {
                if (instrumentInfo.type !== EQUITY) {
                    if (Object.keys(performancePeriods).includes(key)) {
                        const fundValue = (key === 'performance1D') ? quote.dayChangePct : performance[key];
                        
                        performanceData[key] = [
                            {
                                name: quote.nameShort,
                                fund: fundValue ? fundValue : 0,
                                cat: (Object.keys(performanceCats).includes(key) && performance[performanceCats[key]]) ? performance[performanceCats[key]] : 0
                            }
                        ];
                    }
                } else {
                    let performancePeriodData = {};
                    if (performance.hasOwnProperty(key)) {
                        if (key in performancePeriods) {
                            performancePeriodData.name = quote.nameShort;
                            performancePeriodData.return = performance[key];
                        }
                        if (key in performanceCats) {
                            performancePeriodData.benchmarkName = performance.benchmarkName;
                            performancePeriodData.benchmarkReturn = performance[performanceCats[key]];
                        }
                    }
                    performanceData[key] = [ performancePeriodData ];
                }
            });
        } catch (e) {}

        return performanceData;
    }

    const [performanceData] = useState(generatePerformanceData(performance));

    const renderTabs = () => {

        const activeTabClassName = "performance-tab performance-tab-active";
        const tabClassName = "performance-tab";

        return (
            Object.keys(performancePeriods).map((key, index) => {
                return (
                    <span key={index}>
                        <a className={tab === key ? activeTabClassName : tabClassName} href="" onClick={(e) => {
                            e.preventDefault(); 
                            setTab(key);
                        }} data-testid={`${key}Link`}>{performancePeriods[key]}</a>
                        {performancePeriods[key] !== "10Y ann" ? (
                            <>
                            &nbsp;|&nbsp;
                            </>
                        ) : null}
                    </span>
                )
            })
        )
    }

    const displayIcon = (value) => {
        switch (Math.sign(value)) {
            case 1:
                return '+';
            case -1:
                return '-';
            default:
                return '';
        }
    }

    const returnBenchmarkLink = (row) => {
        if (row.benchmarkName in marketReference) {
            return <td scope="row" data-testid={`benchmark${tab}RowHeader`}>
                <a href={`/market-research/markets${marketReference[row.benchmarkName]}`} target="_blank">{row.benchmarkName}</a>
            </td>;
        }
        
        return <td scope="row" data-testid={`benchmark${tab}RowHeader`}>{row.benchmarkName}</td>;
    }

    const renderRows = () => {
        return (
            performanceData[tab].map((row, index) => {
                if (instrumentInfo.type !== EQUITY) {
                    const fundValue = row.fund ? String(row.fund.toFixed(2)).replace('-','') : null;
                    const catValue = row.cat ? String(row.cat.toFixed(2)).replace('-','') : null;
    
                    return <React.Fragment key={index}>
                        <tr>
                            <td scope="row" data-testid={`${tab}FundRowHeader`}>Fund</td>
                            <td className="font-weight-bold" aria-label="Total return (%)" data-testid={`${tab}FundRowValue`}>
                                {fundValue ? <span className={changeClassnameText(row.fund)}>
                                    <span data-testid="fundUpOrDown">{displayIcon(row.fund)}</span> <b>{fundValue}</b>
                                </span> : '-'}
                            </td>
                        </tr>
                        <tr>
                            <td scope="row" data-testid={`${tab}CategoryRowHeader`}>Category</td>
                            <td className="font-weight-bold" aria-label="Total return (%)" data-testid={`${tab}CategoryRowValue`}>
                                {catValue ? <span className={changeClassnameText(row.cat)}>
                                    <span data-testid="catUpOrDown">{displayIcon(row.cat)}</span> <b>{catValue}</b>
                                </span> : '-'}
                            </td>
                        </tr>
                    </React.Fragment>
                } else {
                    const itemReturn = row.return?.toFixed(2).replace('-', '') || 0;
                    const benchmarkReturn = row.benchmarkReturn?.toFixed(2).replace('-', '') || 0;
                    return <React.Fragment key={index}>
                        <tr>
                            <td scope="row" data-testid={`${tab}RowHeader`}>{row.name}</td>
                            <td className="font-weight-bold" aria-label="Total return (%)" data-testid={`${tab}RowValue`}>
                                <span className={changeClassnameText(row.return)}>
                                    <span data-testid="equityUpOrDown">{displayIcon(row.return)}</span> <b>{itemReturn}</b>
                                </span>
                            </td>
                        </tr>
                        {row.benchmarkReturn ? <tr>
                            {returnBenchmarkLink(row)}
                            <td className="font-weight-bold" aria-label="Total return (%)" data-testid={`benchmark${tab}RowValue`}>
                                <span className={changeClassnameText(row.benchmarkReturn)}>
                                    <span data-testid="benchmarkUpOrDown">{displayIcon(row.benchmarkReturn)}</span> <b>{benchmarkReturn}</b>
                                </span>
                            </td>
                        </tr> : null}
                    </React.Fragment>
                }
            })
        )
    }

    const renderPerformance = () => {
        return (
            <div className="ajb-grid-row">
                <div className="ajb-grid-row clearfix">
                    <p className="margin-10 grey-300-text h7 performance-tab-wrapper">
                        {renderTabs()}
                    </p>
                    <hr className="margin-top-0" />
                </div>
                <div className="ajb-grid-row padding-top-0">
                    <table className="data-table data-table--small data-table--scroll margin-0">
                        <thead>
                            <tr>
                                <th></th>
                                <th data-testid="totalReturnTableHead">Total return (%)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {performanceData[tab] && performanceData[tab].length > 0 ? (
                                renderRows()
                            ) : (
                                <tr>
                                    <td scope="row" data-testid="noDataFound">(no data found)</td>
                                    <td></td>
                                </tr>
                            )}
                            {moreLink ? (
                                <tr>
                                    <td colSpan="2" scope="row">
                                        <a href={moreLink} data-testid="performanceMoreLink">More...</a>
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    return <div className="card z-depth-0 margin-0">
        <div className="card-content padding-top-0-sm padding-right-0-sm padding-bottom-30-sm padding-left-0-sm" id="newsContent">
            {performanceData ? (
                <>
                    <h2 className="card-title" data-testid="performanceHeader">Performance {performance.asOfDate ? <span className="h7 float-right float-none-sm" data-testid="performanceDate">{moment(performance.asOfDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}</span> : null}</h2>
                    {renderPerformance()}
                </>
            ) : null}
        </div>
    </div>

}